import type { FrameworkClass } from './framework.d';
import { FrameworkClasses } from './frameworkClasses';

/**
 * Single source of truth for the set of action names that count as
 * "axis input control" methods on framework classes.
 *
 * Collected from every `FrameworkClass.axisActions[].name` so adding a new
 * axis action in `frameworkClasses.ts` automatically flows through to
 * Tree-sitter axis discovery and related binding helpers.
 */

let cachedNames: Set<string> | null = null;
let cachedInputArgByAction: Map<string, string> | null = null;

/** Every distinct `axisActions[].name` declared in `FrameworkClasses`. */
export function getFrameworkAxisActionNames(): Set<string> {
  if (cachedNames) return cachedNames;
  const names = new Set<string>();
  for (const cls of FrameworkClasses as FrameworkClass[]) {
    for (const action of cls.axisActions || []) {
      const name = String(action?.name || '').trim();
      if (name) names.add(name);
    }
  }
  cachedNames = names;
  return names;
}

/**
 * Maps every axis action name to the name of its "input callable" argument
 * (the first argument in `arguments`, which is the one bound to a controller
 * axis getter such as `controller.get_l1_axis`).
 *
 * Falls back silently for axis actions with no arguments declared (entry is
 * omitted from the map).
 */
export function getFrameworkAxisActionInputArgNames(): Map<string, string> {
  if (cachedInputArgByAction) return cachedInputArgByAction;
  const map = new Map<string, string>();
  for (const cls of FrameworkClasses as FrameworkClass[]) {
    for (const action of cls.axisActions || []) {
      const name = String(action?.name || '').trim();
      const firstArg = String(action?.arguments?.[0]?.name || '').trim();
      if (name && firstArg && !map.has(name)) map.set(name, firstArg);
    }
  }
  cachedInputArgByAction = map;
  return map;
}

/** Looks up the input-callable argument name for a given axis action. */
export function getInputArgNameForAxisAction(actionName: string): string | undefined {
  return getFrameworkAxisActionInputArgNames().get(actionName);
}

/** Test-only: reset cached state. */
export function _resetFrameworkAxisActionCacheForTests(): void {
  cachedNames = null;
  cachedInputArgByAction = null;
}
